import type { IPickPageItem, IPickPageSource, IPickPageSourceParams } from '../../types';
import type { InfiniteData } from '@tanstack/react-query';

import type { IToPickPageItemParams } from '../../utils';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { captureSentryError } from '~/exceptions/sentry';
import { shopifyAdminFetcher } from '~/api/utils';
import { useMemo } from 'react';

/**
 * The slice of a cursor connection this hook reads, whatever entity it comes from. Nullable
 * throughout: a platform listing reports an edge, and the list itself, as optional.
 */
interface IPickPageConnection<TNode> {
  edges?: Array<{ node?: TNode | null } | null | undefined> | null;
  pageInfo?: { hasNextPage: boolean; endCursor?: string | null } | null;
}

interface IUsePickPageConnectionSourceParams<
  TResponse,
  TVariables,
  TNode,
> extends IPickPageSourceParams {
  /** Namespaces the cache per entity, since every source shares this hook. */
  queryKey: string;
  document: string;
  /**
   * The query's variables for a given cursor, undefined for the first page. The source spells them
   * itself: every platform listing takes its own parameter input, and some carry an argument of
   * their own besides — the page type a landing listing is narrowed to, say.
   */
  toVariables: (after?: string) => TVariables;
  toConnection: (response: TResponse) => IPickPageConnection<TNode> | null | undefined;
  toItem: (params: IToPickPageItemParams<TNode>) => IPickPageItem;
}

/** Cursor pagination shared by every page-type source that reads a paged API. */
export const usePickPageConnectionSource = <TResponse, TVariables extends object, TNode>({
  queryKey,
  document,
  toVariables,
  searchValue,
  isEnabled,
  templateAssetKeys,
  toConnection,
  toItem,
}: IUsePickPageConnectionSourceParams<TResponse, TVariables, TNode>): IPickPageSource => {
  const queryClient = useQueryClient();
  // Keyed on the first page's variables: what the cursor pages through is the same query throughout.
  const key = [queryKey, toVariables()];

  const query = useInfiniteQuery<TResponse>(
    key,
    (args) => shopifyAdminFetcher<TResponse, TVariables>(document, toVariables(args.pageParam))(),
    {
      enabled: isEnabled,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        const pageInfo = toConnection(lastPage)?.pageInfo;

        return pageInfo?.hasNextPage ? pageInfo.endCursor : undefined;
      },
      onError: (error) =>
        captureSentryError(error, {
          source: 'usePickPageConnectionSource',
          level: 'warning',
          tags: { shopify: 'Shopify' },
          extra: { queryKey },
        }),
    },
  );

  // A disabled source reports no list of its own: its query is idle, but the cache still holds
  // whatever it last read, and a merged search must not carry a type it no longer covers.
  const items = useMemo<IPickPageItem[]>(
    () =>
      isEnabled
        ? (query.data?.pages ?? [])
            .flatMap((page) => toConnection(page)?.edges ?? [])
            .flatMap((edge) => (edge?.node ? [toItem({ node: edge.node, templateAssetKeys })] : []))
        : [],
    // `toConnection`/`toItem` are inline lambdas at every call site, so keying on them would rebuild
    // the list every render — the fetched pages are what actually changes it. The theme's templates
    // are listed: they arrive on their own query, and the rows have to pick them up when they do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data, isEnabled, templateAssetKeys],
  );

  const handleFetchNextPage = () => {
    if (query.isFetchingNextPage || !query.hasNextPage) return;
    query.fetchNextPage();
  };

  // Trims the cache back to page one and refetches it, instead of refreshing every loaded page. Clearing the
  // data instead would let `keepPreviousData` resurrect the result of whatever key this observer held before.
  const handleRefresh = async () => {
    queryClient.setQueryData<InfiniteData<TResponse>>(
      key,
      (current) =>
        current && { pages: current.pages.slice(0, 1), pageParams: current.pageParams.slice(0, 1) },
    );

    await query.refetch({ throwOnError: true });
  };

  // `keepPreviousData` keeps the old list mounted while a new search fetches, so `isLoading` only ever
  // covers the very first load — the previous result is what flags the refetch in between. A disabled
  // query never leaves react-query's loading status, so it only counts while the source is enabled.
  const isLoading = isEnabled && (query.isLoading || (query.isFetching && query.isPreviousData));
  const isError = isEnabled && query.isError;

  return {
    items,
    isLoading,
    isError,
    isSourceEmpty: !isLoading && !isError && !searchValue && isEnabled && items.length === 0,
    isFetchingNextPage: isEnabled && query.isFetchingNextPage,
    hasNextPage: isEnabled && !!query.hasNextPage,
    handleFetchNextPage,
    handleRefresh,
  };
};
